import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import type {
  RelocateToTarget,
  UserLocationPreferences,
  UserLocationPreferencesPatch,
} from "@resume-cloudflare/shared";
import { Plus, Save, Trash2, UserRound } from "lucide-react";
import { useLocationPrefs, usePatchLocationPrefs } from "../lib/profile";
import { Button } from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { Pill } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";

// Defaults match lib/location-match.ts::DEFAULT_USER_PREFS — US-based
// remote-preferring senior. The user can adjust before saving.

interface FormState {
  primary_country: string;
  primary_state: string;
  primary_city: string;
  primary_timezone: string;
  eligible_countries: string; // CSV of ISO codes; parsed on save
  open_to_remote: boolean;
  open_to_hybrid: boolean;
  open_to_onsite: boolean;
  max_in_office_days: string; // "" or "0".."7"
  max_travel_pct: string; // "" or "0".."100"
  willing_to_relocate: boolean;
  relocate_to: RelocateToTarget[];
  relocation_must_be_paid: boolean;
  timezone_overlap_hours_min: string;
}

const EMPTY: FormState = {
  primary_country: "US",
  primary_state: "",
  primary_city: "",
  primary_timezone: "",
  eligible_countries: "US",
  open_to_remote: true,
  open_to_hybrid: true,
  open_to_onsite: false,
  max_in_office_days: "",
  max_travel_pct: "",
  willing_to_relocate: false,
  relocate_to: [],
  relocation_must_be_paid: true,
  timezone_overlap_hours_min: "",
};

function fromServer(p: UserLocationPreferences | null): FormState {
  if (!p) return { ...EMPTY };
  return {
    primary_country: p.primary_country,
    primary_state: p.primary_state ?? "",
    primary_city: p.primary_city ?? "",
    primary_timezone: p.primary_timezone ?? "",
    eligible_countries: p.eligible_countries.join(", "),
    open_to_remote: p.open_to_remote,
    open_to_hybrid: p.open_to_hybrid,
    open_to_onsite: p.open_to_onsite,
    max_in_office_days:
      p.max_in_office_days === null ? "" : String(p.max_in_office_days),
    max_travel_pct: p.max_travel_pct === null ? "" : String(p.max_travel_pct),
    willing_to_relocate: p.willing_to_relocate,
    relocate_to: p.relocate_to,
    relocation_must_be_paid: p.relocation_must_be_paid,
    timezone_overlap_hours_min:
      p.timezone_overlap_hours_min === null
        ? ""
        : String(p.timezone_overlap_hours_min),
  };
}

function parseInt0to(value: string, max: number): number | null | "invalid" {
  if (value.trim() === "") return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0 || n > max) return "invalid";
  return n;
}

function parseEligibleCountries(s: string): string[] | "invalid" {
  const tokens = s
    .split(/[,\s]+/)
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
  if (tokens.length === 0) return "invalid";
  if (!tokens.every((t) => /^[A-Z]{2}$/.test(t))) return "invalid";
  return tokens;
}

function toPatch(s: FormState): UserLocationPreferencesPatch | string {
  if (!/^[A-Z]{2}$/.test(s.primary_country)) return "primary_country must be a 2-letter ISO code";
  const eligible = parseEligibleCountries(s.eligible_countries);
  if (eligible === "invalid") {
    return "Eligible countries must be a comma-separated list of ISO codes (e.g. US, CA)";
  }
  const days = parseInt0to(s.max_in_office_days, 7);
  if (days === "invalid") return "Max in-office days must be 0–7";
  const travel = parseInt0to(s.max_travel_pct, 100);
  if (travel === "invalid") return "Max travel % must be 0–100";
  const tz = parseInt0to(s.timezone_overlap_hours_min, 24);
  if (tz === "invalid") return "Timezone overlap must be 0–24 hours";

  return {
    primary_country: s.primary_country.toUpperCase(),
    primary_state: s.primary_state || null,
    primary_city: s.primary_city || null,
    primary_timezone: s.primary_timezone || null,
    eligible_countries: eligible,
    open_to_remote: s.open_to_remote,
    open_to_hybrid: s.open_to_hybrid,
    open_to_onsite: s.open_to_onsite,
    max_in_office_days: days as number | null,
    max_travel_pct: travel as number | null,
    willing_to_relocate: s.willing_to_relocate,
    relocate_to: s.relocate_to,
    relocation_must_be_paid: s.relocation_must_be_paid,
    timezone_overlap_hours_min: tz as number | null,
  };
}

export default function ProfileLocation() {
  const prefs = useLocationPrefs();
  const patch = usePatchLocationPrefs((data) =>
    `Saved. Re-scored ${data.recompute.updated.toLocaleString()} job${data.recompute.updated === 1 ? "" : "s"}.`,
  );

  const serverState = useMemo(
    () => fromServer(prefs.data?.location_preferences ?? null),
    [prefs.data?.location_preferences],
  );
  const [local, setLocal] = useState<FormState>(serverState);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => setLocal(serverState), [serverState]);

  const dirty = JSON.stringify(local) !== JSON.stringify(serverState);
  const canSave = dirty && !patch.isPending;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setLocal((prev) => ({ ...prev, [key]: value }));
    setValidationError(null);
  }

  function onSave() {
    const result = toPatch(local);
    if (typeof result === "string") {
      setValidationError(result);
      return;
    }
    setValidationError(null);
    patch.mutate(result);
  }

  function addRelocateTarget() {
    set("relocate_to", [...local.relocate_to, { city: "", country: "US" }]);
  }
  function updateRelocateTarget(idx: number, patch: Partial<RelocateToTarget>) {
    set(
      "relocate_to",
      local.relocate_to.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    );
  }
  function removeRelocateTarget(idx: number) {
    set("relocate_to", local.relocate_to.filter((_, i) => i !== idx));
  }

  return (
    <section className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Location preferences
        </h1>
        <p className="mt-1 text-sm text-neutral-400">
          Drives the <code className="rounded bg-neutral-900 px-1">/jobs</code>{" "}
          filter "Eligible where I live" and the{" "}
          <code className="rounded bg-neutral-900 px-1">location_fit</code>{" "}
          score axis. Saving recomputes scores for every job in pure SQL —
          no AI re-inference.
        </p>
      </header>

      <Card>
        <CardHeader
          title="Where you are"
          description="Used as the default for ‘where I live’ and to colour location cells in /jobs."
        />
        <CardBody>
          {prefs.isLoading ? (
            <FieldSkeletons rows={4} />
          ) : prefs.error ? (
            <EmptyState
              tone="danger"
              title="Couldn't load location preferences"
              description="Try refreshing in a moment."
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Country (ISO-3166 alpha-2)"
                value={local.primary_country}
                onChange={(v) => set("primary_country", v.toUpperCase().slice(0, 2))}
                placeholder="US"
              />
              <Field
                label="State / region"
                value={local.primary_state}
                onChange={(v) => set("primary_state", v.toUpperCase().slice(0, 2))}
                placeholder="TX"
              />
              <Field
                label="City"
                value={local.primary_city}
                onChange={(v) => set("primary_city", v)}
                placeholder="Austin"
              />
              <Field
                label="Timezone (IANA)"
                value={local.primary_timezone}
                onChange={(v) => set("primary_timezone", v)}
                placeholder="America/Chicago"
              />
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Eligibility"
          description="Where you can work without sponsorship. ISO codes, comma-separated."
        />
        <CardBody>
          <Field
            label="Eligible countries"
            value={local.eligible_countries}
            onChange={(v) => set("eligible_countries", v)}
            placeholder="US, CA"
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Work mode"
          description="Combine as you see fit; jobs that don't match any selected mode get a low location_fit."
        />
        <CardBody>
          <div className="space-y-2">
            <Toggle
              label="Open to remote"
              checked={local.open_to_remote}
              onChange={(v) => set("open_to_remote", v)}
            />
            <Toggle
              label="Open to hybrid"
              checked={local.open_to_hybrid}
              onChange={(v) => set("open_to_hybrid", v)}
            />
            <Toggle
              label="Open to onsite"
              checked={local.open_to_onsite}
              onChange={(v) => set("open_to_onsite", v)}
            />
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field
              label="Max in-office days / wk (0 = none, blank = no preference)"
              value={local.max_in_office_days}
              onChange={(v) => set("max_in_office_days", v.replace(/\D/g, "").slice(0, 1))}
              placeholder="2"
            />
            <Field
              label="Max travel % (blank = no preference)"
              value={local.max_travel_pct}
              onChange={(v) => set("max_travel_pct", v.replace(/\D/g, "").slice(0, 3))}
              placeholder="25"
            />
            <Field
              label="Min timezone overlap (hours)"
              value={local.timezone_overlap_hours_min}
              onChange={(v) =>
                set("timezone_overlap_hours_min", v.replace(/\D/g, "").slice(0, 2))
              }
              placeholder="4"
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Relocation"
          description="Cities you'd consider moving to. Used by the hybrid/onsite scorer."
        />
        <CardBody className="space-y-3">
          <Toggle
            label="Willing to relocate"
            checked={local.willing_to_relocate}
            onChange={(v) => set("willing_to_relocate", v)}
          />
          <Toggle
            label="Relocation must be paid"
            checked={local.relocation_must_be_paid}
            onChange={(v) => set("relocation_must_be_paid", v)}
          />
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-neutral-400">
                Cities you'd move to
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={addRelocateTarget}
                leadingIcon={<Plus size={12} aria-hidden />}
              >
                Add
              </Button>
            </div>
            {local.relocate_to.length === 0 ? (
              <p className="text-xs text-neutral-500">
                None. Add one if you'd consider moving for the right role.
              </p>
            ) : (
              <ul className="space-y-2">
                {local.relocate_to.map((t, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Input
                      placeholder="City"
                      value={t.city}
                      onChange={(e) => updateRelocateTarget(i, { city: e.target.value })}
                    />
                    <Input
                      placeholder="US"
                      value={t.country}
                      onChange={(e) =>
                        updateRelocateTarget(i, {
                          country: e.target.value.toUpperCase().slice(0, 2),
                        })
                      }
                      className="w-20"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRelocateTarget(i)}
                      leadingIcon={<Trash2 size={12} aria-hidden />}
                      aria-label={`Remove ${t.city || "row"}`}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-4">
        <div className="text-xs text-neutral-500">
          Personal info lives on{" "}
          <Link
            to="/profile"
            className="inline-flex items-center gap-1 text-neutral-300 underline-offset-4 hover:underline"
          >
            <UserRound size={12} aria-hidden /> Profile
          </Link>
          .
        </div>
        <div className="flex items-center gap-3">
          {validationError ? <Pill tone="warning">{validationError}</Pill> : null}
          <Button
            variant="primary"
            size="sm"
            loading={patch.isPending}
            disabled={!canSave}
            leadingIcon={<Save size={12} aria-hidden />}
            onClick={onSave}
          >
            Save preferences
          </Button>
        </div>
      </div>
    </section>
  );
}

function FieldSkeletons({ rows }: { rows: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-400">
        {label}
      </label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
      />
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-200">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-accent-500"
      />
      <span>{label}</span>
    </label>
  );
}

